import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerPadding,
	Layout,
} from '@volkswagen-onehub/components-core';
import { InteractionLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { OfertaCardProvider } from '../context/ofertas-card-context';
import { CustomBreakpoints } from '../helpers/breakpoints';
import { getFinanciacionType, getPagoFinanciado, getPagoUnico } from '../helpers/ofertas-helper';
import { scrollToDisclaimerText } from '../helpers/ofertas-scroll';
import { useOfertasState } from '../hooks/use-ofertas-state';
import { CarlineWithOfertas, Promocion } from '../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../tracking/use-tracking-manager';
import { ContentLayer } from './layer/content-layer';
import { renderLayerWrapper } from './layer/layer-wrapper';
import { OfertaLayer } from './layer/oferta-layer';
import { OfertaCardsContainer } from './oferta-card/oferta-cards-container';
import { ModelFrame } from './oferta-model/model-frame';
import { TitleBackground } from './oferta-model/model-title-background/model-title-background';
import { DesktopSelector } from './selectors/desktop-selector';
import { MobileSelector } from './selectors/mobile-selector';
import { ModelListDesktop } from './selectors/model-list/model-list-desktop';
import {
	DesktopSelectorContainer,
	ModelFrameWrapper,
	OfertasMobileStyledContainer,
	OfertasSection,
	OfertasStickyContainer,
} from './ui/ofertas-styles';

export const Ofertas: React.FC<{
	carlines: CarlineWithOfertas[];
	hasOneCarline: boolean;
	handleFilteredCarlines: (selectedFilter: string) => void;
	selectedGamaFilter: string | null;
	selectedMotorizacionFilter: string | null;
	setSelectedGamaFilter: React.Dispatch<React.SetStateAction<string | null>>;
	setSelectedMotorizacionFilter: React.Dispatch<React.SetStateAction<string | null>>;
	isCallMeNowOpen: boolean;
}> = ({
	carlines,
	hasOneCarline,
	handleFilteredCarlines,
	selectedGamaFilter,
	selectedMotorizacionFilter,
	setSelectedGamaFilter,
	setSelectedMotorizacionFilter,
	isCallMeNowOpen,
}) => {
	const [selectedPromotion, setSelectedPromotion] = useState<Promocion | undefined>(undefined);

	/*Iniciamos state de control de layer */
	const [isLayerVisible, setIsLayerVisible] = useState(false);
	const [isLayerActive, setIsLayerActive] = useState<boolean>(false);
	const [interactionLayerSize, setInteractionLayerSize] = useState<InteractionLayerSizeV2>(InteractionLayerSizeV2.B);

	const trackingManager = useTrackingManager();

	const {
		selectedOferta,
		isAutoScrollingRef,
		setSelectedOferta,
		activeCarlineIndex,
		setActiveCarlineIndex,
		ofertasRef,
		numberOfCarlines,
		carlineSelectedURLRef,
	} = useOfertasState(carlines, trackingManager);

	const handleLayerOnClick = () => setIsLayerActive(!isLayerActive);

	const handleScrollToDisclaimer = (reference: string) => scrollToDisclaimerText(reference, isAutoScrollingRef);

	/* Inicializamos referencias */
	const sectionRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {};

	// Animation variants
	const variants = {
		hidden: { opacity: 0, y: -50 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.5, ease: 'easeInOut' },
		},
	};

	const handleTrackingOnClick = (carline: CarlineWithOfertas, index: number) => {
		carlineSelectedURLRef.current = true;

		setActiveCarlineIndex(index);
		setSelectedOferta(carline.ofertas[0]);

		const pagoFinanciado = getPagoFinanciado(carline.ofertas[0].datosEconomicos);

		trackingManager.trackOfertasModelSelectClick({
			CarlineId: carline.code.toString(),
			CarlineName: carline.title,
			SalesgroupName: carline.ofertas[0].carline?.salesgroup.title,
			ModelId: carline.ofertas[0].configuracion.motorizacion.code?.split('-')[0],
			ModelName: carline.ofertas[0].carline?.salesgroup.model.name,
			ModelYear: carline.ofertas[0].carline?.salesgroup.year.toString(),
			PriceRateType: pagoFinanciado ? getFinanciacionType(pagoFinanciado) : undefined,
			PriceRateTotal: pagoFinanciado?.precio ?? undefined,
			PriceRateCurrency: 'EUR',
			PriceTotalTotal: getPagoUnico(carline.ofertas[0].datosEconomicos)?.precio ?? undefined,
			PriceTotalCurrency: 'EUR',
		});

		trackingManager.trackOfertasVehiclePageload({
			CarlineId: carline.code.toString(),
			CarlineName: carline.title,
			SalesgroupName: carline.ofertas[0].carline?.salesgroup.title,
			ModelId: carline.ofertas[0].configuracion.motorizacion.code?.split('-')[0],
			ModelName: carline.ofertas[0].carline?.salesgroup.model.name,
			ModelYear: carline.ofertas[0].carline?.salesgroup.year.toString(),
			PriceRateType: pagoFinanciado ? getFinanciacionType(pagoFinanciado) : undefined,
			PriceRateTotal: pagoFinanciado?.precio ?? undefined,
			PriceRateCurrency: 'EUR',
			PriceTotalTotal: getPagoUnico(carline.ofertas[0].datosEconomicos)?.precio ?? undefined,
			PriceTotalCurrency: 'EUR',
		});
	};

	useEffect(() => {
		const checkScreenSize = () => {
			if (window.innerWidth < 1200) {
				setInteractionLayerSize(InteractionLayerSizeV2.D);
			}

			if (window.innerWidth > 1199) {
				setInteractionLayerSize(InteractionLayerSizeV2.B);
			}
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => window.removeEventListener('resize', checkScreenSize);
	}, []);

	if (!carlines || carlines.length === 0) {
		return null;
	}

	const visibleSelectedOferta = selectedOferta ?? carlines[0]?.ofertas[0];
	const activeCarline = carlines[activeCarlineIndex] ?? carlines[0];

	if (!visibleSelectedOferta || !activeCarline) {
		return null;
	}

	return (
		<OfertaCardProvider
			value={{
				hasOneCarline,
				isCallMeNowOpen,
				isLayerVisible,
				setIsLayerVisible,
				selectedOferta: visibleSelectedOferta,
				setSelectedOferta,
				setSelectedPromotion,
				selectedPromotion,
				handleLayerOnClick,
				handleScrollToDisclaimer,
			}}
		>
			{/* CALCULADORA DESACTIVADA, SI SE RETOMA SE DEBE FIXEAR */}
			{/* <FinanceLayer
				financeData={financeData}
				setIsFinanceLayerVisible={setIsFinanceLayerVisible}
				isFinanceLayerVisible={isFinanceLayerVisible}
				financeLayerRef={financeLayerRef}
				setIsLayerVisible={setIsLayerVisible}
			/> */}
			<>
				<OfertasSection
					className="ofertas__ofertas-section"
					numberOfCarlines={numberOfCarlines}
					hasOneCarline={hasOneCarline}
					ref={ofertasRef}
				>
					<ContentLayer
						className="ofertas__content-layer"
						isLayerVisible={isLayerVisible}
						layerSize={interactionLayerSize}
					>
						<OfertaLayer
							className="ofertas__oferta-layer"
							setIsLayerVisible={setIsLayerVisible}
							selectedOferta={visibleSelectedOferta}
							activeCarline={activeCarline}
							isCallMeNowOpen={isCallMeNowOpen}
							handleScrollToDisclaimer={handleScrollToDisclaimer}
						/>
					</ContentLayer>

					{activeCarlineIndex >= 0 && (
						<OfertasStickyContainer className="ofertas__ofertas-sticky-container" hasOneCarline={hasOneCarline}>
							<TitleBackground
								hasOneCarline={hasOneCarline}
								activeCarlineIndex={activeCarlineIndex}
								carlines={carlines}
							/>

							{/* OFERTAS VERSION TABLET Y DESKTOP */}
							<BreakpointWrapper min={Breakpoints.b960}>
								<Layout
									data-testid="ofertas__layout-ofertas-desktop"
									appearance={{
										[Breakpoints.default]: [{ name: 'a', columns: 24 }],
										[Breakpoints.b960]: hasOneCarline
											? [
													{ name: '.', columns: 0 },
													{ name: 'a', columns: 24 },
													{ name: '.', columns: 0 },
												]
											: [
													{ name: '.', columns: 1 },
													{ name: 'a', columns: 22 },
													{ name: '.', columns: 1 },
												],
									}}
								>
									{!hasOneCarline && (
										<>
											<DesktopSelectorContainer className="ofertas__desktop-selector-container">
												<DesktopSelector
													handleFilteredCarlines={handleFilteredCarlines}
													selectedGamaFilter={selectedGamaFilter}
													selectedMotorizacionFilter={selectedMotorizacionFilter}
													setSelectedGamaFilter={setSelectedGamaFilter}
													setSelectedMotorizacionFilter={setSelectedMotorizacionFilter}
													setActiveCarlineIndex={setActiveCarlineIndex}
												/>
											</DesktopSelectorContainer>

											{/* LISTA DE CARLINES */}
											<ModelListDesktop
												hasOneCarline={hasOneCarline}
												carlines={carlines}
												activeCarlineIndex={activeCarlineIndex}
												handleTrackingOnClick={handleTrackingOnClick}
											/>
										</>
									)}

									{/* CARD DE LA OFERTA Y CARROUSEL DE IMAGENES */}
									<ModelFrameWrapper className="ofertas__model-frame-wrapper" hasOneCarline={hasOneCarline}>
										<motion.section
											key={activeCarlineIndex}
											id={carlines[activeCarlineIndex].name}
											initial="hidden"
											animate="visible"
											ref={sectionRefs[carlines[activeCarlineIndex].name]}
											variants={variants}
										>
											<ModelFrame carline={carlines[activeCarlineIndex]} hasOneCarline={hasOneCarline} />
										</motion.section>
									</ModelFrameWrapper>

									{/* CARD OFERTA EN VERSION TABLET */}
									<BreakpointWrapper min={Breakpoints.b960} max={CustomBreakpoints.DESKTOP}>
										<OfertaCardsContainer
											className="ofertas__ofertas-card-container-tablet"
											isMobile={false}
											carline={carlines[activeCarlineIndex]}
										/>
									</BreakpointWrapper>
								</Layout>
							</BreakpointWrapper>

							{/* OFERTAS VERSION MOBILE */}
							<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
								<Container data-testid="ofertas__mobile-container" wrap="always">
									<OfertasMobileStyledContainer
										data-testid="ofertas__model-selector-container-Mobile"
										padding={ContainerPadding.grid001}
										horizontalAlign={'center'}
										stretchContent
									>
										{!hasOneCarline && (
											<MobileSelector
												carlines={carlines}
												activeCarlineIndex={activeCarlineIndex}
												setActiveCarlineIndex={setActiveCarlineIndex}
												setSelectedOferta={setSelectedOferta}
												selectedGamaFilter={selectedGamaFilter}
												selectedMotorizacionFilter={selectedMotorizacionFilter}
												setSelectedGamaFilter={setSelectedGamaFilter}
												setSelectedMotorizacionFilter={setSelectedMotorizacionFilter}
												carlineSelectedURLRef={carlineSelectedURLRef}
											/>
										)}
									</OfertasMobileStyledContainer>

									{activeCarlineIndex >= 0 && carlines[activeCarlineIndex] && (
										<motion.section
											key={carlines[activeCarlineIndex]?.id || 'empty'}
											id={carlines[activeCarlineIndex]?.name}
											initial="hidden"
											animate="visible"
											variants={variants}
											style={{ height: '100%' }}
										>
											<OfertaCardsContainer
												className="ofertas__ofertas-card-container-mobile"
												carline={carlines[activeCarlineIndex]}
												isMobile={true}
											/>
										</motion.section>
									)}
								</Container>
							</BreakpointWrapper>
						</OfertasStickyContainer>
					)}
				</OfertasSection>

				{isLayerActive && selectedPromotion?.descripcion && renderLayerWrapper(handleLayerOnClick, selectedPromotion)}
			</>
		</OfertaCardProvider>
	);
};
